import {
  ChevronLeft,
  ChevronRight,
  FileDownload,
  Search,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import {
  MANAGEMENT_TABLE_HEADER_COLOR,
  ManagementTableActionCell,
  ManagementTableHeaderCell,
  managementTableRowSx,
} from '../common/ManagementTable';
import { TableNoData } from '../common/TableEmptyState';
import { CustomerIntakeTemplateVersion } from '../../types/CustomerIntake.type';
import {
  CustomerIntakeForm,
  CustomerIntakeResponseDetail,
  CustomerIntakeResponseOverviewListItem,
  getCustomerIntakeErrorMessage,
  getCustomerIntakeResponse,
  listAllCustomerIntakeResponses,
  downloadAllCustomerIntakeResponses,
  listCustomerIntakeForms,
  listCustomerIntakeTemplates,
} from '../../utils/customerIntakeApi';
import { useTranslation } from '../../i18n';
import CustomerIntakeConversionActions from './CustomerIntakeConversionActions';
import CustomerIntakeConversionStatus from './CustomerIntakeConversionStatus';

const formatSubmittedAt = (date: string) =>
  new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatValue = (value: string | string[] | boolean | number | null) => {
  if (value === null || value === '') return '—';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value;
};

type CustomerIntakeResponseField =
  CustomerIntakeResponseDetail['sections'][number]['fields'][number];

const renderResponseField = (field: CustomerIntakeResponseField) => (
  <Box
    key={field.fieldId}
    sx={{
      p: 1.5,
      borderRadius: 1,
      backgroundColor: 'action.hover',
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {field.label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {field.displayValue || formatValue(field.value)}
    </Typography>
  </Box>
);

const CustomerIntakeResponsesOverview = () => {
  const { t } = useTranslation();
  const [responses, setResponses] = useState<
    CustomerIntakeResponseOverviewListItem[]
  >([]);
  const [selectedResponse, setSelectedResponse] =
    useState<CustomerIntakeResponseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [downloading, setDownloading] = useState(false);
  // Form / version filters (pair naturally with the multi-form tabs).
  const [forms, setForms] = useState<CustomerIntakeForm[]>([]);
  const [formId, setFormId] = useState<number | ''>('');
  const [versions, setVersions] = useState<CustomerIntakeTemplateVersion[]>([]);
  const [versionId, setVersionId] = useState<number | ''>('');

  const loadResponses = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listAllCustomerIntakeResponses({
        page: page + 1,
        limit: rowsPerPage,
        search,
        formId: formId || undefined,
        versionId: versionId || undefined,
      });
      setResponses(result.items);
      setTotalItems(result.metadata.totalItems);
      setError('');
    } catch (loadError) {
      setError(
        getCustomerIntakeErrorMessage(
          loadError,
          'Unable to load customer intake responses.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, formId, versionId]);

  useEffect(() => {
    void loadResponses();
  }, [loadResponses]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadAllCustomerIntakeResponses({
        search,
        formId: formId || undefined,
        versionId: versionId || undefined,
      });
    } catch (downloadError) {
      setError(
        getCustomerIntakeErrorMessage(
          downloadError,
          'Unable to download customer intake responses.',
        ),
      );
    } finally {
      setDownloading(false);
    }
  };

  // Load the form list once for the filter dropdown.
  useEffect(() => {
    listCustomerIntakeForms()
      .then(setForms)
      .catch(() => {
        /* filter is optional — a failure here shouldn't block the table */
      });
  }, []);

  // When a form is chosen, load its versions for the version dropdown.
  useEffect(() => {
    if (!formId) {
      setVersions([]);
      return;
    }
    let active = true;
    listCustomerIntakeTemplates({ formId, limit: 100 })
      .then((result) => {
        if (active) setVersions(result.items);
      })
      .catch(() => {
        if (active) setVersions([]);
      });
    return () => {
      active = false;
    };
  }, [formId]);

  const handleFormChange = (nextFormId: number | '') => {
    setFormId(nextFormId);
    setVersionId('');
    setPage(0);
  };

  const handleVersionChange = (nextVersionId: number | '') => {
    setVersionId(nextVersionId);
    setPage(0);
  };

  const handleViewResponse = async (
    response: CustomerIntakeResponseOverviewListItem,
  ) => {
    setLoadingDetail(true);
    try {
      const detail = await getCustomerIntakeResponse(
        String(response.versionId),
        response.id,
      );
      setSelectedResponse(detail);
      setError('');
    } catch (detailError) {
      setError(
        getCustomerIntakeErrorMessage(
          detailError,
          'Unable to load the submitted information.',
        ),
      );
    } finally {
      setLoadingDetail(false);
    }
  };

  const consentFields =
    selectedResponse?.sections.flatMap((section) =>
      section.fields.filter((field) => field.type === 'consent'),
    ) || [];

  const selectedResponseIndex = selectedResponse
    ? responses.findIndex(
        (response) =>
          response.id === selectedResponse.id &&
          response.versionId === selectedResponse.versionId,
      )
    : -1;

  const handleNavigateResponse = (direction: -1 | 1) => {
    if (selectedResponseIndex < 0) return;

    const nextIndex = selectedResponseIndex + direction;
    if (nextIndex < 0 || nextIndex >= responses.length) return;

    void handleViewResponse(responses[nextIndex]);
  };

  return (
    <>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void loadResponses()}
            >
              Try again
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <Box
          p={2}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <TextField
            size="medium"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            placeholder={t('surveyResponses.searchPlaceholder')}
            inputProps={{ 'aria-label': 'Search customer intake responses' }}
            sx={{ width: { xs: '100%', sm: 360, md: 440 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                size: 'small',
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="response-form-filter">
              {t('surveyResponses.form')}
            </InputLabel>
            <Select
              labelId="response-form-filter"
              label="Form"
              value={formId === '' ? '' : String(formId)}
              onChange={(event) =>
                handleFormChange(
                  event.target.value === '' ? '' : Number(event.target.value),
                )
              }
            >
              <MenuItem value="">
                <em>{t('surveyResponses.allForms')}</em>
              </MenuItem>
              {forms.map((form) => (
                <MenuItem key={form.id} value={String(form.id)}>
                  {form.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={!formId}>
            <InputLabel id="response-version-filter">
              {t('surveyResponses.version')}
            </InputLabel>
            <Select
              labelId="response-version-filter"
              label="Version"
              value={versionId === '' ? '' : String(versionId)}
              onChange={(event) =>
                handleVersionChange(
                  event.target.value === '' ? '' : Number(event.target.value),
                )
              }
            >
              <MenuItem value="">
                <em>{t('surveyResponses.allVersions')}</em>
              </MenuItem>
              {versions.map((version) => (
                <MenuItem key={version.id} value={String(version.id)}>
                  {`Version ${version.version}`}
                  {version.status === 'ACTIVE' ? ' · Active' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={t('surveyResponses.download')}>
            <span>
              <IconButton
                color="primary"
                aria-label={t('surveyResponses.download')}
                onClick={handleDownload}
                disabled={downloading || totalItems === 0}
              >
                {downloading ? <CircularProgress size={20} /> : <FileDownload />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {loading ? (
          <Stack spacing={1} alignItems="center" sx={{ p: 5 }}>
            <CircularProgress size={28} />
            <Typography color="text.secondary">
              Loading submitted customers...
            </Typography>
          </Stack>
        ) : (
          <>
            <TableContainer>
              <Table
                size="small"
                padding="checkbox"
                sx={{ minWidth: 1120 }}
                aria-label="All customer intake responses"
              >
                <TableHead>
                  <TableRow
                    sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}
                  >
                    <ManagementTableHeaderCell>#</ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('surveyResponses.colSubmittedAt')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('surveyResponses.colFormVersion')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('surveyResponses.colFullName')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('surveyResponses.colPhone')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('surveyResponses.colEmail')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('surveyResponses.colConversion')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell sticky>
                      {t('surveyResponses.colActions')}
                    </ManagementTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {responses.map((response, index) => (
                    <TableRow
                      key={`${response.versionId}-${response.id}`}
                      sx={managementTableRowSx(
                        selectedResponse?.id === response.id,
                      )}
                    >
                      <TableCell
                        align="center"
                        sx={{ width: 64, whiteSpace: 'nowrap' }}
                      >
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatSubmittedAt(response.submittedAt)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 240 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {response.templateName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {response.version}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={
                              response.status === 'ACTIVE' ? 'Active' : 'Draft'
                            }
                            color={
                              response.status === 'ACTIVE'
                                ? 'success'
                                : 'default'
                            }
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {response.customerName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {response.phone || '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {response.email || '—'}
                      </TableCell>
                      <TableCell sx={{ minWidth: 270 }}>
                        <CustomerIntakeConversionStatus
                          status={response.conversionStatus}
                        />
                      </TableCell>
                      <ManagementTableActionCell
                        selected={selectedResponse?.id === response.id}
                      >
                        <Tooltip title="View submitted information">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              aria-label={`View response from ${response.customerName || 'customer'}`}
                              onClick={() => void handleViewResponse(response)}
                              disabled={loadingDetail}
                            >
                              <VisibilityOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ManagementTableActionCell>
                    </TableRow>
                  ))}
                  {responses.length === 0 && (
                    <TableNoData message="No submitted responses found" />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
            />
          </>
        )}
      </Paper>

      <Dialog
        fullWidth
        maxWidth="md"
        open={Boolean(selectedResponse)}
        onClose={() => setSelectedResponse(null)}
        scroll="paper"
      >
        <DialogTitle>{t('surveyResponses.submittedInformation')}</DialogTitle>
        <DialogContent dividers>
          {selectedResponse && (
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={0.75}>
                  <Typography variant="h6" color="secondary.main">
                    {selectedResponse.customerName || 'Unnamed customer'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Version {selectedResponse.version} · Submitted{' '}
                    {formatSubmittedAt(selectedResponse.submittedAt)}
                  </Typography>
                  <Typography variant="body2">
                    {selectedResponse.phone || '—'} ·{' '}
                    {selectedResponse.email || '—'}
                  </Typography>
                </Stack>
              </Card>

              {selectedResponse.sections.map((section) => {
                const regularFields = section.fields.filter(
                  (field) => field.type !== 'consent',
                );

                if (regularFields.length === 0) return null;

                return (
                  <Box key={section.sectionId}>
                    <Typography
                      variant="subtitle1"
                      color="secondary.main"
                      fontWeight={700}
                      sx={{ mb: 1 }}
                    >
                      {section.title}
                    </Typography>
                    <Stack spacing={1}>
                      {regularFields.map(renderResponseField)}
                    </Stack>
                  </Box>
                );
              })}

              {consentFields.length > 0 && (
                <Box
                  sx={{
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack spacing={1}>
                    {consentFields.map(renderResponseField)}
                  </Stack>
                </Box>
              )}

              <CustomerIntakeConversionActions
                response={selectedResponse}
                onConversionCompleted={() => void loadResponses()}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            px: 2,
            gap: 1,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ minWidth: 0 }}
          >
            <IconButton
              size="small"
              aria-label={t('surveyResponses.previousResponse')}
              onClick={() => handleNavigateResponse(-1)}
              disabled={
                loadingDetail ||
                selectedResponseIndex <= 0 ||
                selectedResponseIndex < 0
              }
            >
              <ChevronLeft />
            </IconButton>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 110, textAlign: 'center' }}
            >
              {selectedResponseIndex >= 0
                ? t('surveyResponses.responsePosition', {
                    current: selectedResponseIndex + 1,
                    total: responses.length,
                  })
                : ''}
            </Typography>
            <IconButton
              size="small"
              aria-label={t('surveyResponses.nextResponse')}
              onClick={() => handleNavigateResponse(1)}
              disabled={
                loadingDetail ||
                selectedResponseIndex < 0 ||
                selectedResponseIndex >= responses.length - 1
              }
            >
              <ChevronRight />
            </IconButton>
          </Stack>
          <Button onClick={() => setSelectedResponse(null)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerIntakeResponsesOverview;
